import { IPlatformAPI } from '@wix/native-components-infra/dist/src/types/types';
import { CACHE_TTL_MS } from '../../constants';
import { SiteStore } from '@wix/wixstores-client-storefront-sdk';

type CachedItem<T> = { timestamp: number; item: T };

export class ViewerStorage<T> {
  private readonly cacheKey: string;
  private readonly storage: IPlatformAPI['storage'];
  private readonly siteStore: SiteStore;

  constructor({
    cacheKey,
    clearOnRefresh = true,
    storage,
    siteStore,
  }: {
    cacheKey: string;
    clearOnRefresh?: boolean;
    storage: IPlatformAPI['storage'];
    siteStore: SiteStore;
  }) {
    this.cacheKey = cacheKey;
    this.storage = storage;
    this.siteStore = siteStore;

    if (clearOnRefresh) {
      this.clearOnReload();
      siteStore.location.onChange(() => {
        this.storage.session.setItem(
          this.getLastUrlCacheKey(),
          this.siteStore.location.url,
        );
      });
    }
  }

  public get(): T | undefined {
    let item;
    try {
      item = this.storage.session.getItem(this.cacheKey);
    } catch {
      item = undefined;
    }
    if (!item) {
      return;
    }

    let parsed: CachedItem<T>;
    try {
      parsed = JSON.parse(item) as CachedItem<T>;
    } catch {
      return;
    }

    const isExpired = Date.now() - parsed.timestamp > CACHE_TTL_MS;
    if (isExpired || this.isPreviewMode()) {
      this.remove();
      return;
    }

    return parsed.item;
  }

  public set(item: T) {
    const cachedItem: CachedItem<T> = {
      item,
      timestamp: Date.now(),
    };

    try {
      this.storage.session.setItem(this.cacheKey, JSON.stringify(cachedItem));
    } catch {
      this.remove();
    }
  }

  public remove() {
    this.storage.session.removeItem(this.cacheKey);
  }

  private clearOnReload() {
    const lastUrlCacheKey = this.getLastUrlCacheKey();
    const url = this.siteStore.location.url;
    let lastUrl;
    let hadError = false;
    try {
      lastUrl = this.storage.session.getItem(lastUrlCacheKey);
    } catch {
      hadError = true;
    }
    const isReload = url === lastUrl;
    if (isReload || hadError) {
      this.remove();
      this.storage.session.removeItem(lastUrlCacheKey);
    }
    this.storage.session.setItem(lastUrlCacheKey, url);
  }

  private getLastUrlCacheKey() {
    return `${this.cacheKey}-lastUrl`;
  }

  private isPreviewMode() {
    return (
      new URL(this.siteStore.location.url).searchParams.get('previewMode') ===
      'true'
    );
  }
}
